<template>
  <div class="flex flex-row overflow-auto bg-gray-900">
    <div class="component-graph-renderer-top relative">
      <div>
        <svg class="absolute overflow-hidden" :style='svgDimensions'>
          <defs>
            <marker id='head' orient="auto"
                              markerWidth='3' markerHeight='4'
                              refX='0.1' refY='2'>
              <!-- triangle pointing right (+x) -->
              <path d='M0,0 V4 L2,2 Z' :fill="linkColor"/>
            </marker>
          </defs>


          <g v-for='(extraArrow,i) in extraArrows' :key='i'>
            <path marker-end='url(#head)'
                    :stroke-width='linkWidth'
                    class="svg-link"
                    fill='none' :stroke='linkColor'
                    :d='extraArrow.d' />
          </g>

          <g v-for='graphLink in graphLinks' :key='graphLink.id'>
            <path :key='graphLink.d'
                  marker-end='url(#head)'
                  :stroke-width='linkWidth'
                  class="svg-link"
                  fill='none' :stroke='linkColor'
                  :d='graphLink.d' />
          </g>
        </svg>

        <div v-if='childComponent' class="absolute w-32 mx-4 my-2" >
          <component :is='childComponent'
                     :transform='transform'
                     :parent-transform='parentTransform'
                     :transforms-and-source='transformsAndSource' 
                     :partutil='partutil'
                     />

        </div>
        <div ref='allComponents' 
             v-for='graphNode in graphNodes'
             class="absolute flex flex-col justify-center"
             :style='graphNode.style'
             :key='graphNode.component.id'
             :data-newlyadded='newlyAdded[graphNode.component.id] ? "true" : undefined'
             :class='isHightlightComponent(graphNode.component)'
             >
          <pbc-button v-if='graphNode.component.id !== fromComponent.id' class="absolute right-0 top-0" @click='onRemove(graphNode.component)' >
            <delete :size='24' />
          </pbc-button>
          <div @click='edit(graphNode.component)' class='w-32 h-20 bg-gray-100 shadow rounded cursor-pointer'>
          <condensed :component='graphNode.component'
                     :transform='transform'
                     :transforms-and-source='transformsAndSource'
                     :partutil='partutil'
                     :treeutil='treeutil'
                     :disable-icon='false'
                               />
          </div>
          <div class="flex flex-col">
            <pbc-button v-for='linkTitle in graphNode.linkTitles' class="text-xs font-thin flex-none mx-1 border border-gray-600" :key='linkTitle.title'>
              {{linkTitle.title}}
              <chevron-right class="ml-auto" />
            </pbc-button>
            <pbc-button v-if='graphNode.hasSlots' class="pbc-primary-color text-xs font-semibold flex-none mx-1 border border-gray-600" @click='onAdd(graphNode.component)'>
              Add
              <plus-circle class="ml-auto" />
            </pbc-button>
          </div>

          <slot name='after'
                class='relative'
                :component='graphNode.component' />
        </div>
      </div>
      <slot v-if='editingComponent.id'
            name='editing'
            :is-selected-component='true'
            :component='editingComponent'
            :shadow-component='shadowEditingComponent'
            :move-to='moveTo'
            :select='select'
            :close='close'
            />

    </div>
  </div>
</template>
<script>
import Condensed from '@components/molecules/Condensed.vue';
import ChevronRight from '@icons/ChevronRight.vue';
import PlusCircle from '@icons/PlusCircle.vue';
import Delete from '@icons/Delete.vue';
import dagre from 'dagre';
import * as ComponentsDb from '@edit/tree/componentsdb.js';
import { cloneDeep, uniqBy, debounce, differenceBy } from 'lodash';

export default {
  props:{
    transform: {
      type: Object,
      required: true
    },
    transformsAndSource: {
      type: Object,
      required: true
    },
    partutil: {
      type: Object,
      required:true
    },
    treeutil:{
      type: Object,
      required: true
    },
    initialEditComponent: {
      type: Object,
      required: false
    },
    childComponent: {
      type: Object,
      required: false
    },
    parentTransform: {
      type: Object,
      required: false
    },
    selectionFirstMode: {
      type: Boolean,
      default: false
    }
  },
  data() {
    const fromComponent = this.treeutil.components[0];
    const graphNodes = [];
    const graphLinks = [];
    const svgDimensions = '';

    let marginx = 20;
    let marginy = 20;
    const extraArrows = [];
    if (this.childComponent) {
      marginy = 100;
      const cw = 220;
      const ch = 60;
      const d = `M${cw/2},${ch/2} L${cw/2},${ch*1.5}`;
      extraArrows.push({d});
    }
    return {
      fromComponent,
      graphNodes,
      graphLinks,
      svgDimensions,
      marginx,
      marginy,
      linkColor:'rgb(203, 213, 224)',
      linkWidth:8,
      shadowEditingComponent:{},
      newlyAdded:{},
      editingComponent:this.initialEditComponent || {},
      extraArrows,
      firstLoad:true
    }
  },
  components: {
    Condensed,
    PlusCircle,
    Delete,
    ChevronRight
  },
  mounted() {
    this.debouncedRefresh = debounce(this.refresh, 100);
    this.$eventbus.on('closeAddComponentWizard', this.doneAdding);
    this.$eventbus.on('addRecommendation', this.doneAdding);
    if (this.selectionFirstMode) {
      this.edit(this.fromComponent);
    }
  },
  methods: {
    edit(component) {
      if (this.editingComponent.id === component.id) {
        const shadowComponent = this.shadowEditingComponent;
        this.$emit("edit", {component,shadowComponent});
      } else {
        this.$set(this, 'editingComponent', component);
      }
    },
    onRemove(component) {
      this.$emit("remove", {component});
      this.$set(this, 'editingComponent', {});
    },
    makeGraph(fromComponent) {

      const width = 192;
      const height = 80;

      const g = new dagre.graphlib.Graph();
      g.setGraph({
        //rankdir:isWideScreen ? 'TB' : 'LR'
        //nodesep:240,
        nodesep:height * 2.0,
        ranksep:240,
        edgesep:20,
        rankdir:'LR',
        align: 'UL',
        //ranker: 'tight-tree',
        ranker: 'tight-tree',
        marginx: this.marginx,
        marginy: this.marginy,
        width,
        height
      });
      g.setDefaultEdgeLabel(function() { return {}; });

      const t1 = new Date().getTime();
      const {components,links} = this.partutil.findComponentsAndLinksFrom(fromComponent);
      const t2 = new Date().getTime();

      components.forEach( (component) => {
        g.setNode(component.id, {id:component.id});
      });

      links.forEach( (link) => {
        g.setEdge(link.source, link.target);
      });

      dagre.layout(g);

      let right = -99999;
      let bottom =  -99999;

      const positions = {};
      const linksFrom = {};
      const linkPositions = {};

      g.nodes().forEach( (id) => {
        const p = g.node(id);
        positions[id] = p;
      });

      links.forEach( (link) => {
        const {id, source, target} = link;
        if (!linksFrom[source]) {
          linksFrom[source] = []
        }
        const component = this.partutil.getComponentById(source);
        const linkTitle = ComponentsDb.createLinkTitle(component, link);
        linksFrom[source].push(linkTitle);

      });

      const graphNodes = g.nodes().map( (id) => {
        const p = g.node(id);

        if (p.x + width + 40> right) {
          right = p.x+ width + 40;
        }
        if (p.y +height > bottom) {
          bottom = p.y+height *2;
        }

        const component = this.treeutil.findComponentById(id);
        const style = this.makeStyle(p, component);

        const linkTitles = uniqBy(
          (linksFrom[component.id] || []),
          ({title}) => {
            return title;
          });

        linkPositions[component.id] = {};
        linkTitles.forEach( (linkTitle, i) => {
          linkPositions[component.id][linkTitle.title] = i;
        });

        const hasSlots = this.hasSlots(component);

        return {
          style,
          component,
          linkTitles,
          hasSlots
        }
      });


      const graphLinks = links.map( (link) => {
        const {source,target,id} = link;

        const component = this.partutil.getComponentById(link.source);
        const linkTitle = ComponentsDb.createLinkTitle(component, link).title;
        let linkMarginX = width + 20;
        const totalLinks = Object.keys(linkPositions[link.source]).length;
        const maxDifferentLinks = 4;
        const linkPosition = linkPositions[link.source][linkTitle];
        if (totalLinks > 1) {
          const fromEnd = Math.min(maxDifferentLinks, totalLinks - linkPosition - 1);

          linkMarginX = width + 10 + fromEnd * 10;
        }

        const linkSourceMarginY = (height/2) + 55 + linkPosition * 35;
        const linkToMarginY = 20;
        const arrowWidth = 10;

        const from = positions[source];
        const to = positions[target];

        const p0 = {
            x:from.x + width/2,
            y:from.y + linkSourceMarginY
        };
        const p1 = {
          x:from.x + linkMarginX,
          y:from.y + linkSourceMarginY
        };
        const p2 = {
          x:from.x + linkMarginX,
          y:to.y + linkToMarginY
        };
        const p3 = {
          x:to.x - arrowWidth,
          y:to.y + linkToMarginY
        };

        const lines = [[p0,p1],[p1,p2],[p2,p3]].map( ([from, to]) => {
          return `M${from.x},${from.y} L${to.x},${to.y}`;
        });

        let d = `M${p0.x},${p0.y} `;
        d += [p1,p2,p3].map( ({x,y}) => {
          return `L${x},${y}`;
        }).join(' ');

        return {
          id,
          from,
          to,
          lines,
          d
        }
      });

      const svgDimensions = {
        width: (right + this.marginx) + 'px',
        height: (bottom + this.marginy) + 'px'
      }
      return {
        graphNodes,
        graphLinks,
        svgDimensions
      }


    },
    makeStyle({x,y}, component) {
      return {
        left: x + 'px',
        top: y + 'px'
      }
    },
    select(component) {
      // do nothing, or scroll to it maybe?
    },
    close() {
      this.editingComponent = {};
    },
    moveTo({component}) {
      // do nothing, or scroll to it maybe?
    },
    onAdd(component) {
      this.$emit('add', {
        component
      });
    },
    refresh(editingComponent) {
      console.log('refreshing graph');
      this.newlyAdded = {};

      this.graphNodes.forEach( ({component:{id}}) => {
        this.$set(this.newlyAdded, id, false);
      });
      const oldIds = this.graphNodes.map( ({component: {id}}) => {
        return {id};
      })
      const {graphLinks, graphNodes, svgDimensions} = this.makeGraph(editingComponent);
      console.log('now nodes', graphNodes.length, 'and links', graphLinks.length);
      // find new nodes, so that we can highlight / scroll to them
      this.graphNodes = graphNodes;
      this.graphLinks = graphLinks;
      this.svgDimensions = svgDimensions;

      const newIds = this.graphNodes.map( ({component: {id}}, i) => {
        return {
          id,
          i
        }
      });

      this.newComponents = differenceBy(newIds, oldIds, ({id}) => {
        return id;
      });
      this.$set(this, 'newlyAdded', {});
      if (!this.firstLoad && this.newComponents.length === 1) {
        this.newComponents.forEach( ({id,i}) => {
          this.$set(this.newlyAdded, id, true);

          if (this.selectionFirstMode) {
            const component = this.partutil.getComponentById(id);
            this.$set(this, 'editingComponent', component);
          }
        });
      }
      this.firstLoad = false;

    },
    doneAdding() {
      const newComponents = this.newComponents;
      if (newComponents.length === 1) {
        setTimeout( () => {
          newComponents.forEach( ({id,i}) => {
            this.$set(this.newlyAdded, id, true);
            const el = this.$refs.allComponents.find( (el) => {
              return el.dataset.newlyadded === "true";
            });
            if (!el) {
              console.warn('could not find element to scroll to', i);
              return;
            } else {
              el.scrollIntoView({
                behavior:'smooth',
                block:'start'
              });
            }
          });
        }, 500);
      }
    },
    hasSlots(component) {
      const slots = ComponentsDb.createSlots(component, this.partutil, this.transform);
      return slots.length;
    },
    isHightlightComponent(component) {
      if (this.selectionFirstMode) {
        return (this.editingComponent.id === component.id ) ?  "component-graph-newly-added" : "";

      } else {
        return this.newlyAdded[component.id] ? "component-graph-newly-added" : "";
      }
    }
  },
  watch:{
    fromComponent: {
      handler(nv) {
        this.refresh(nv);
      },
      immediate: true
    },
    editingComponent:{
      handler(nv, ov) {
        this.$emit("change-editing-component", nv);
        this.shadowEditingComponent = cloneDeep(nv);
      },
      immediate: true
    },
    'treeutil.components.length'() {
      console.log('le refresh!!!');
      this.debouncedRefresh(this.fromComponent);
    },
    'treeutil.links.length'() {
      console.log('le refresh!!!');
      this.debouncedRefresh(this.fromComponent);
    }
  }
}
</script>
<style>
.svg-link {
stroke-linecap:butt;
stroke-linejoin:miter;
}

</style>
